import React, { useState, useEffect, useReducer, useContext } from "react";
import openSocket from "socket.io-client";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import clsx from "clsx";
import {MenuItem, FormControl, Select, IconButton, Tooltip, useMediaQuery} from "@material-ui/core";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import LockOpenOutlinedIcon from '@material-ui/icons/LockOpenOutlined';
import DirectionsRunIcon from '@material-ui/icons/DirectionsRun';

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ClientModal from "../../components/ClientModal";
import ConfirmationModal from "../../components/ConfirmationModal/";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import {format, parseISO} from "date-fns";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    ...theme.scrollbarStyles,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    border: "1px solid rgba(15, 23, 42, 0.06)",
    boxShadow: "0 16px 40px rgba(15, 23, 42, 0.08)",
    overflowX: "auto",
    [theme.breakpoints.down("sm")]: {
      overflowX: "hidden",
    },
  },
  mainContainer: {
    overflowX: "auto",
    [theme.breakpoints.down("sm")]: {
      overflowX: "hidden",
    },
    padding: theme.spacing(0, 2, 2),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0, 1, 2),
    },
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      margin: theme.spacing(0.5, 0),
    },
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  headerMeta: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    boxShadow: "0 6px 14px rgba(15, 23, 42, 0.12)",
  },
  statusOk: {
    backgroundColor: "#16A34A",
    color: "#FFFFFF",
  },
  statusBlock: {
    backgroundColor: "#D97706",
    color: "#FFFFFF",
  },
  statusOff: {
    backgroundColor: "#DC2626",
    color: "#FFFFFF",
  },
  statusUnknown: {
    backgroundColor: "#475569",
    color: "#FFFFFF",
  },
  tableLink: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  rowStripe: {
    backgroundColor: "rgba(15, 23, 42, 0.03)",
  },
  rowHover: {
    "&:hover": {
      backgroundColor: "rgba(56, 189, 248, 0.08)",
    },
  },
  nameCell: {
    maxWidth: "50ch",
  },
  nameText: {
    display: "block",
    maxWidth: "50ch",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  dateCell: {
    width: 110,
    whiteSpace: "nowrap",
  },
  linkCell: {
    width: 180,
    maxWidth: 180,
  },
  linkText: {
    display: "block",
    maxWidth: 180,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  filterInput: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      margin: theme.spacing(0.5, 0),
    },
  },
  addButton: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      marginTop: theme.spacing(0.5),
    },
  },
  filtersWrapper: {
    width: "100%",
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      gap: theme.spacing(0.5),
    },
  },
  filterToggle: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  mobileList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    padding: theme.spacing(0.5),
  },
  mobileCard: {
    padding: theme.spacing(1.5),
    borderRadius: 14,
    border: "1px solid rgba(15, 23, 42, 0.06)",
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
  },
  mobileRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  mobileLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  mobileValue: {
    fontWeight: 600,
  },
  mobileActions: {
    display: "flex",
    gap: theme.spacing(0.5),
    justifyContent: "flex-end",
    marginTop: theme.spacing(1),
  },
}));

const Clients = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [clients, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [sort, setSort] = useState("name");
  const [order, setOrder] = useState("ASC");
  const [countClients, setCountClients] = useState(0);
  const [status, setStatus] = useState("all");
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, sort, order, status]);

  useEffect(() => {
    if (!isMobile) {
      setFiltersOpen(true);
    }
  }, [isMobile]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchClients = async () => {
        try {
          const { data } = await api.get("/clients/", {
            params: { searchParam, pageNumber, all: true, sort, order, status },
          });
          dispatch({ type: "LOAD_CONTACTS", payload: data.clients });
          setHasMore(data.hasMore);
          setCountClients(data.count);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchClients();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, sort, order, countClients, status]);

  useEffect(() => {
    const socket = openSocket(window.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL);

    socket.on("contact", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const cpfMask = (value) => {
    if (!value)
      return "";
    let splited = value.split("");
    return `${splited[0]}${splited[1]}${splited[2]}.${splited[3]}${splited[4]}${splited[5]}.${splited[6]}${splited[7]}${splited[8]}-${splited[9]}${splited[10]}`
  }

  const cnpjMask = (value) => {
    if (!value)
      return "";
    let splited = value.split("");
    return `${splited[0]}${splited[1]}.${splited[2]}${splited[3]}${splited[4]}.${splited[5]}${splited[6]}${splited[7]}/${splited[8]}${splited[9]}${splited[10]}${splited[11]}-${splited[12]}${splited[13]}`;
  }

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactModalOpen(true);
  };

  const handleDeleteContact = async (clientId) => {
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success("Cliente excluído com sucesso!");
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleChangeStatus = async (clientId, status) => {
    if (clientId) {
      await api.put(`/clients/${clientId}`, {
        status
      });
    }
  }

  const getStatusInfo = (status) => {
    if (status === "OK") {
      return { label: "Liberado", className: classes.statusOk };
    }
    if (status === "BLOCK") {
      return { label: "Bloqueado", className: classes.statusBlock };
    }
    if (status === "OFF") {
      return { label: "Cancelado", className: classes.statusOff };
    }
    return { label: status || "-", className: classes.statusUnknown };
  };

  return (
      <div className={classes.mainContainer}>
        <MainContainer>
          <ClientModal
              open={contactModalOpen}
              onClose={handleCloseContactModal}
              aria-labelledby="form-dialog-title"
              clientId={selectedContactId}>
          </ClientModal>
          <ConfirmationModal
              title="Remover esse cliente?"
              open={confirmOpen}
              onClose={setConfirmOpen}
              onConfirm={(e) =>
                  handleDeleteContact(deletingContact.id)
              }
          >
            {i18n.t("contacts.confirmationModal.deleteMessage")}
          </ConfirmationModal>
          <MainHeader
            title="Clientes"
            subtitle="Gerencie clientes, permissões e status"
            actions={
              <MainHeaderButtonsWrapper>
                {isMobile ? (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setFiltersOpen((prev) => !prev)}
                    className={classes.filterToggle}
                  >
                    {filtersOpen ? "Ocultar filtros" : "Mostrar filtros"}
                  </Button>
                ) : null}
                {(!isMobile || filtersOpen) ? (
                  <div className={classes.filtersWrapper}>
                    <span className={classes.headerMeta}>Total: {countClients}</span>
                    <span className={classes.headerMeta}>Ordenar por:</span>
                    <FormControl className={classes.formControl} variant="outlined" margin="dense">
                      <Select
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          variant="outlined"
                          value={sort}
                          onChange={(e)=> setSort(e.target.value)}
                      >
                        <MenuItem value="name">Nome</MenuItem>
                        <MenuItem value="selledAt">Venda</MenuItem>
                        <MenuItem value="maxUser">Usuário</MenuItem>
                        <MenuItem value="maxConnection">Whatsapp</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl className={classes.formControl} variant="outlined" margin="dense">
                      <Select
                          labelId="demo-simple-select2-label"
                          id="demo-simple2-select"
                          variant="outlined"
                          value={order}
                          onChange={(e)=> setOrder(e.target.value)}
                      >
                        <MenuItem value="ASC">ASC</MenuItem>
                        <MenuItem value="DESC">DESC</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl className={classes.formControl} variant="outlined" margin="dense">
                      <Select
                          labelId="demo-simple-select3-label"
                          id="demo-simple3-select"
                          variant="outlined"
                          value={status}
                          onChange={(e)=> setStatus(e.target.value)}
                      >
                        <MenuItem value="all">Todos</MenuItem>
                        <MenuItem value="ok">Liberados</MenuItem>
                        <MenuItem value="block">Bloqueados</MenuItem>
                        <MenuItem value="off">Cancelados</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                        placeholder={i18n.t("contacts.searchPlaceholder")}
                        type="search"
                        value={searchParam}
                        onChange={handleSearch}
                        className={classes.filterInput}
                        margin="dense"
                        size="small"
                        InputProps={{
                          startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon style={{ color: "gray" }} />
                              </InputAdornment>
                          ),
                        }}
                    />

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenContactModal}
                        className={classes.addButton}
                    >
                      Adicionar Cliente
                    </Button>
                  </div>
                ) : null}
              </MainHeaderButtonsWrapper>
            }
          />
          <Paper
              className={classes.mainPaper}
              variant="outlined"
              onScroll={handleScroll}
          >
            {isMobile ? (
              <div className={classes.mobileList}>
                {clients.map((client) => {
                  const statusInfo = getStatusInfo(client.status);
                  return (
                    <Paper key={client.id} className={classes.mobileCard} variant="outlined">
                      <div className={classes.mobileRow}>
                        <div>
                          <div className={classes.mobileLabel}>Cliente</div>
                          <Tooltip title={client.name || ""} arrow interactive>
                            <div className={classes.nameText}>{client.name}</div>
                          </Tooltip>
                        </div>
                        <span className={clsx(classes.statusPill, statusInfo.className)}>
                          {statusInfo.label}
                        </span>
                      </div>
                      <div className={classes.mobileRow}>
                        <div>
                          <div className={classes.mobileLabel}>CNPJ</div>
                          <div className={classes.mobileValue}>
                            {client.cnpj.length === 11 ? cpfMask(client.cnpj) : cnpjMask(client.cnpj)}
                          </div>
                        </div>
                        <div>
                          <div className={classes.mobileLabel}>Telefone</div>
                          <div className={classes.mobileValue}>{client.phone}</div>
                        </div>
                      </div>
                      <div className={classes.mobileRow}>
                        <div>
                          <div className={classes.mobileLabel}>Venda</div>
                          <div className={classes.mobileValue}>
                            {format(parseISO(client.selledAt), "dd/MM/yyyy")}
                          </div>
                        </div>
                        <div>
                          <div className={classes.mobileLabel}>Link</div>
                          <Tooltip title={client.link || ""} arrow interactive>
                            <a
                              className={clsx(classes.tableLink, classes.linkText)}
                              href={client.link}
                              rel={"noopener noreferrer"}
                              target={"_blank"}
                            >
                              {client.link}
                            </a>
                          </Tooltip>
                        </div>
                      </div>
                      <div className={classes.mobileRow}>
                        <div>
                          <div className={classes.mobileLabel}>Users</div>
                          <div className={classes.mobileValue}>{client.maxUser}</div>
                        </div>
                        <div>
                          <div className={classes.mobileLabel}>Whatsapps</div>
                          <div className={classes.mobileValue}>{client.maxConnection}</div>
                        </div>
                      </div>
                      <div className={classes.mobileActions}>
                        <Tooltip title="Liberar/Bloquear" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleChangeStatus(client.id, client.status === "OK" ? "BLOCK" : "OK")}
                          >
                            {client.status !== "OK" ? (<LockOpenOutlinedIcon />) : (<LockOutlinedIcon />)}
                          </IconButton>
                        </Tooltip>

                          <Tooltip title="Cancelar" arrow>
                            <span>
                              <IconButton
                                  size="small"
                                  disabled={client.status === "OFF"}
                                  onClick={() => handleChangeStatus(client.id, "OFF")}
                              >
                                <DirectionsRunIcon />
                              </IconButton>
                            </span>
                          </Tooltip>

                        <Tooltip title="Editar" arrow>
                          <IconButton
                            size="small"
                            onClick={() => hadleEditContact(client.id)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>

                        <Can
                          role={user.profile}
                          perform="contacts-page:deleteContact"
                          yes={() => (
                            <Tooltip title="Remover" arrow>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setConfirmOpen(true);
                                  setDeletingContact(client);
                                }}
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        />
                      </div>
                    </Paper>
                  );
                })}
                {loading && <TableRowSkeleton avatar columns={8} />}
              </div>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {/*<TableCell padding="checkbox" />*/}
                    <TableCell>Nome</TableCell>
                    <TableCell align="center">CNPJ</TableCell>
                    <TableCell align="center">Telefone</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center" className={classes.dateCell}>Data Venda</TableCell>
                    <TableCell align="center" className={classes.linkCell}>Link</TableCell>
                    <TableCell align="center">Users</TableCell>
                    <TableCell align="center">Whatsapps</TableCell>
                    <TableCell align="center">
                      {i18n.t("contacts.table.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <>
                    {clients.map((client, index) => {
                      const statusInfo = getStatusInfo(client.status);
                      return (
                        <TableRow
                          key={client.id}
                          className={clsx(
                            index % 2 === 1 && classes.rowStripe,
                            classes.rowHover
                          )}
                        >
                          {/*<TableCell style={{ paddingRight: 0 }}></TableCell>*/}
                          <TableCell className={classes.nameCell}>
                            <Tooltip title={client.name || ""} arrow interactive>
                              <span className={classes.nameText}>{client.name}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">
                            {client.cnpj.length === 11 ? cpfMask(client.cnpj) : cnpjMask(client.cnpj)}
                          </TableCell>
                          <TableCell align="center">{client.phone}</TableCell>
                          <TableCell align="center">
                            <span
                              className={clsx(classes.statusPill, statusInfo.className)}
                            >
                              {statusInfo.label}
                            </span>
                          </TableCell>
                          <TableCell align="center" className={classes.dateCell}>
                            {format(parseISO(client.selledAt), "dd/MM/yyyy")}
                          </TableCell>
                          <TableCell align="center" className={classes.linkCell}>
                            <Tooltip title={client.link || ""} arrow interactive>
                              <a
                                className={clsx(classes.tableLink, classes.linkText)}
                                href={client.link}
                                rel={"noopener noreferrer"}
                                target={"_blank"}
                              >
                                {client.link}
                              </a>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">{client.maxUser}</TableCell>
                          <TableCell align="center">{client.maxConnection}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="Liberar/Bloquear" arrow>
                              <IconButton
                                  size="small"
                                  onClick={() => handleChangeStatus(client.id, client.status === "OK" ? "BLOCK" : "OK")}
                              >
                                {client.status !== "OK" ? (<LockOpenOutlinedIcon />) : (<LockOutlinedIcon />)}
                              </IconButton>
                            </Tooltip>

                        <Tooltip title="Cancelar" arrow>
                          <span>
                            <IconButton
                              size="small"
                              disabled={client.status === "OFF"}
                              onClick={() => handleChangeStatus(client.id, "OFF")}
                            >
                              <DirectionsRunIcon />
                            </IconButton>
                          </span>
                        </Tooltip>

                            <Tooltip title="Editar" arrow>
                              <IconButton
                                  size="small"
                                  onClick={() => hadleEditContact(client.id)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>

                            <Can
                                role={user.profile}
                                perform="contacts-page:deleteContact"
                                yes={() => (
                                    <Tooltip title="Remover" arrow>
                                      <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            setConfirmOpen(true);
                                            setDeletingContact(client);
                                          }}
                                      >
                                        <DeleteOutlineIcon />
                                      </IconButton>
                                    </Tooltip>
                                )}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {loading && <TableRowSkeleton avatar columns={8} />}
                  </>
                </TableBody>
              </Table>
            )}
          </Paper>
        </MainContainer>
      </div>
  );
};

export default Clients;

